/**
 * The review of one AI reply, offered under it and never started on its own: compare
 * against the human agent's reply or investigate the trace independently.
 * The judge stays inline in full; the investigation keeps only its answer here and opens
 * the rest in the report dialog, where a fault report about the pipeline has room to read.
 */

import { useState } from 'react';
import { AlertTriangle, GitCompareArrows } from 'lucide-react';
import { RunReportDialog } from '@/modules/review/report/run-report-dialog';
import { VerdictDisclosure } from '@/modules/review/verdict-disclosure';
import { PillButton } from '../ui/pill-button';
import { ComposeReply } from './compose-reply';
import { GapInvestigationView } from './gap-investigation-view';
import { ReportViews } from './report-views';
import { ReviewInvite } from './review-invite';
import { useRunReview } from './use-run-review';

interface RunReviewProps {
  /** The run this reply came from; the review is keyed by it. */
  requestId: string;
  aiReply: string;
  /** The ticket the loaded scenario replays; null when the run was not scenario-backed, or its scenario is fabricated. */
  ticketId: string | null;
  /** The loaded scenario was fabricated, so no human ever replied to it. */
  fabricated?: boolean;
  /** Whether that ticket has a human agent reply at all. */
  hasAgentReply: boolean;
  /** The run's Langfuse trace; without one there is nothing to investigate. */
  traceUrl?: string;
  customerQuestion?: string;
}

/** A stage that did not conclude, and the way to ask for it again. */
const ReviewFailure = ({
  message,
  onRetry,
  retryDisabled = false,
}: {
  message: string;
  onRetry?: () => void;
  retryDisabled?: boolean;
}) => (
  <div className='flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-3.5 py-2.5 animate-in fade-in-0 duration-300'>
    <AlertTriangle className='size-3.5 shrink-0 text-amber-600' aria-hidden />
    <p className='min-w-0 flex-1 text-[13px] leading-4.5 text-amber-900'>{message}</p>
    {onRetry && (
      <PillButton className='shrink-0' onClick={onRetry} disabled={retryDisabled}>
        Try again
      </PillButton>
    )}
  </div>
);

export const RunReview = ({
  requestId,
  aiReply,
  ticketId,
  fabricated = false,
  hasAgentReply,
  traceUrl,
  customerQuestion,
}: RunReviewProps) => {
  const { judge, investigation, runJudge, runInvestigation } = useRunReview(requestId);
  // Open when the verdict lands, since it was just asked for; foldable from then on.
  const [open, setOpen] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  // Held so a failed comparison hands the reply back instead of asking for it again.
  const [authoredReply, setAuthoredReply] = useState('');

  if (!aiReply.trim()) return null;

  const compare = (humanResponse?: string) => {
    if (humanResponse) setAuthoredReply(humanResponse);
    void runJudge({ ticketId, aiResponse: aiReply, customerQuestion, humanResponse });
  };

  // Offer the ticket's own reply when there is one; only otherwise ask the reader for one.
  const canDerive = Boolean(ticketId) && hasAgentReply;
  const behind = fabricated ? 'fabricated' : ticketId ? 'ticket' : null;

  const result = judge.status === 'done' ? judge.result : null;
  const shadow = judge.status === 'idle' ? (
    canDerive ? (
      <ReviewInvite icon={GitCompareArrows} title='A human agent answered this ticket'
        description='Compare it against the reply the AI just generated.' action='Compare' onAction={() => compare()} />
    ) : <ComposeReply behind={behind} onCompare={compare} />
  ) : judge.status === 'failed' ? (
    <div className='flex flex-col gap-3'>
      <ReviewFailure message={judge.message} onRetry={canDerive ? () => compare() : undefined} />
      {!canDerive && <ComposeReply behind={behind} defaultValue={authoredReply} onCompare={compare} />}
    </div>
  ) : (
    <VerdictDisclosure comparison={result?.comparison ?? null} bucket={result?.bucket ?? null}
      aiReply={aiReply} humanReply={result?.humanResponse.join('\n\n')}
      judging={judge.status === 'judging'} open={open} onToggle={() => setOpen((prev) => !prev)} />
  );
  const investigate = () => void runInvestigation({ langfuseTraceUrl: traceUrl ?? '', bucket: result?.bucket ?? null, ticketId });
  return (
    <>
      <ReportViews key={requestId} shadow={shadow} gap={
        <GapInvestigationView
          investigation={investigation.status === 'done' ? investigation.investigation : null}
          running={investigation.status === 'running'}
          steps={investigation.status === 'idle' ? [] : investigation.steps}
          failure={investigation.status === 'failed' ? investigation.reason : null}
          canInvestigate={Boolean(traceUrl)} onInvestigate={investigate} onOpenReport={() => setReportOpen(true)}
        />
      } />
      {investigation.status === 'done' && (
        <RunReportDialog open={reportOpen} onOpenChange={setReportOpen} investigation={investigation.investigation} />
      )}
    </>
  );
};
